import { ImageTypeEnum } from '@routellers/types/dist/upload/enum/image-type.enum';
import {
    ActionIcon,
    Box,
    Group,
    Image,
    Loader,
    Stack,
    Text,
    TextInput,
    Tooltip,
    UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
    IconPhoto,
    IconPhotoPlus,
    IconStar,
    IconStarFilled,
    IconTrash,
    IconUpload,
    IconUser,
    IconX,
} from '@tabler/icons-react';
import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, apiErrorMessage } from '../../api';

import { ImageLightbox } from './image-lightbox';

type AvatarProps = {
    variant: 'avatar';
    label?: string;
    description?: string;
    value: string | null | undefined;
    onChange: (url: string | null) => void;
    imageType?: ImageTypeEnum;
    disabled?: boolean;
    size?: number;
};

type GalleryProps = {
    variant: 'gallery';
    label?: string;
    description?: string;
    value: string[];
    onChange: (urls: string[]) => void;
    /** Index of cover photo; defaults to 0 when unset. */
    coverIndex?: number | null;
    onCoverIndexChange?: (index: number | null) => void;
    imageType?: ImageTypeEnum;
    disabled?: boolean;
    max?: number;
};

type Props = AvatarProps | GalleryProps;

const ACCENT = 'var(--mantine-color-brand-6, var(--mantine-color-blue-6))';
const BORDER = 'var(--mantine-color-gray-3)';
const SURFACE = 'var(--mantine-color-gray-0)';

async function uploadImage(file: File, imageType: ImageTypeEnum): Promise<string> {
    const data = new FormData();

    data.append('file', file);
    data.append('type', imageType);

    const { data: res } = await api.uploads.uploadImage({
        data,
        onUploadProgress: () => {},
    });

    return res.url;
}

async function ingestImageUrl(raw: string, imageType: ImageTypeEnum): Promise<string> {
    const url = new URL(raw.trim());

    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
        throw new Error('unsupported');
    }

    const { data } = await api.uploads.uploadImageFromUrl({
        url: url.toString(),
        type: imageType,
    });

    if (!data?.url) throw new Error('empty');

    return data.url;
}

const isImageFile = (file: File) =>
    file.type.startsWith('image/') ||
    /\.(jpe?g|png|webp|gif|svg)$/i.test(file.name);

const DropShell = ({
    active,
    disabled,
    onBrowse,
    onDropFiles,
    children,
    minHeight,
}: {
    active: boolean;
    disabled?: boolean;
    onBrowse: () => void;
    onDropFiles: (files: File[]) => void;
    children: React.ReactNode;
    minHeight?: number;
}) => {
    const [over, setOver] = useState(false);

    return (
        <Box
            onDragEnter={(e) => {
                e.preventDefault();
                if (!disabled) setOver(true);
            }}
            onDragOver={(e) => {
                e.preventDefault();
                if (!disabled) setOver(true);
            }}
            onDragLeave={(e) => {
                e.preventDefault();
                setOver(false);
            }}
            onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                if (disabled) return;
                const files = Array.from(e.dataTransfer.files || []);

                if (files.length) onDropFiles(files);
            }}
            onClick={() => {
                if (!disabled && !active) onBrowse();
            }}
            style={{
                position: 'relative',
                minHeight,
                borderRadius: 16,
                border: `1.5px dashed ${over ? ACCENT : BORDER}`,
                background: over
                    ? 'color-mix(in srgb, var(--mantine-color-brand-0, #e7f5ff) 80%, white)'
                    : SURFACE,
                transition: 'border-color 160ms ease, background 160ms ease, transform 160ms ease',
                transform: over ? 'scale(1.01)' : 'none',
                cursor: disabled ? 'not-allowed' : active ? 'default' : 'pointer',
                overflow: 'hidden',
            }}
        >
            {children}
        </Box>
    );
};

const AvatarUpload = ({
    label,
    description,
    value,
    onChange,
    imageType = ImageTypeEnum.AVATAR,
    disabled,
    size = 112,
}: Omit<AvatarProps, 'variant'>) => {
    const { t } = useTranslation();
    const resolvedLabel = label ?? t('common.avatar');
    const inputRef = useRef<HTMLInputElement>(null);
    const addingRef = useRef(false);
    const [uploading, setUploading] = useState(false);
    const [urlDraft, setUrlDraft] = useState('');
    const [previewOpen, setPreviewOpen] = useState(false);

    const handleFiles = useCallback(
        async (files: File[]) => {
            const file = files.find(isImageFile);

            if (!file) {
                notifications.show({
                    color: 'red',
                    message: t('components.photoUpload.dropImageFile'),
                });

                return;
            }

            setUploading(true);

            try {
                const url = await uploadImage(file, imageType);

                onChange(url);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
            } finally {
                setUploading(false);
            }
        },
        [imageType, onChange, t],
    );

    const addUrl = async () => {
        const next = urlDraft.trim();

        if (!next || addingRef.current) return;

        addingRef.current = true;
        setUploading(true);

        try {
            onChange(await ingestImageUrl(next, imageType));
            setUrlDraft('');
        } catch {
            notifications.show({
                color: 'red',
                message: t('components.photoUpload.urlUnreadable'),
            });
        } finally {
            addingRef.current = false;
            setUploading(false);
        }
    };

    return (
        <Stack gap='xs'>
            {(resolvedLabel || description) && (
                <Stack gap={2}>
                    {resolvedLabel && (
                        <Text size='sm' fw={500}>
                            {resolvedLabel}
                        </Text>
                    )}
                    {description && (
                        <Text size='xs' c='dimmed'>
                            {description}
                        </Text>
                    )}
                </Stack>
            )}

            <Group align='flex-end' gap='md' wrap='nowrap'>
                <Box style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
                    <Box
                        onDragEnter={(e) => {
                            e.preventDefault();
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                        }}
                        onDrop={(e) => {
                            e.preventDefault();
                            if (disabled || uploading) return;
                            const files = Array.from(e.dataTransfer.files || []);

                            if (files.length) void handleFiles(files);
                        }}
                        onClick={() => {
                            if (disabled || uploading) return;
                            if (value) {
                                setPreviewOpen(true);
                                return;
                            }
                            inputRef.current?.click();
                        }}
                        style={{
                            width: size,
                            height: size,
                            borderRadius: '50%',
                            overflow: 'hidden',
                            display: 'grid',
                            placeItems: 'center',
                            background: 'var(--mantine-color-gray-1)',
                            border: `1.5px dashed ${value ? 'transparent' : BORDER}`,
                            cursor: disabled || uploading ? 'default' : 'pointer',
                            boxShadow: value
                                ? '0 8px 22px color-mix(in srgb, #000 12%, transparent)'
                                : 'none',
                        }}
                    >
                        {value ? (
                            <Image
                                src={value}
                                alt={resolvedLabel}
                                w={size}
                                h={size}
                                fit='cover'
                                referrerPolicy='no-referrer'
                            />
                        ) : (
                            <Stack align='center' gap={4}>
                                {uploading ? (
                                    <Loader size={22} />
                                ) : (
                                    <IconUser
                                        size={28}
                                        stroke={1.4}
                                        color='var(--mantine-color-gray-5)'
                                    />
                                )}
                                <Text size='10px' c='dimmed' ta='center' px={8}>
                                    {t('components.photoUpload.dropOrClick')}
                                </Text>
                            </Stack>
                        )}
                    </Box>

                    {value && !disabled && (
                        <Group
                            gap={4}
                            style={{
                                position: 'absolute',
                                right: -4,
                                bottom: -4,
                            }}
                        >
                            <Tooltip label={t('components.photoUpload.replace')}>
                                <ActionIcon
                                    size='sm'
                                    radius='xl'
                                    variant='filled'
                                    color='dark'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        inputRef.current?.click();
                                    }}
                                    disabled={uploading}
                                    aria-label={t('components.photoUpload.replace')}
                                >
                                    {uploading ? <Loader size={12} color='white' /> : <IconUpload size={12} />}
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip label={t('components.photoUpload.remove')}>
                                <ActionIcon
                                    size='sm'
                                    radius='xl'
                                    variant='filled'
                                    color='red'
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onChange(null);
                                    }}
                                    disabled={uploading}
                                    aria-label={t('components.photoUpload.remove')}
                                >
                                    <IconX size={12} />
                                </ActionIcon>
                            </Tooltip>
                        </Group>
                    )}
                </Box>

                <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                    <Text size='xs' c='dimmed'>
                        {t('components.photoUpload.squarePhotoHint')}
                    </Text>
                    <Group gap='xs' wrap='nowrap'>
                        <TextInput
                            size='xs'
                            placeholder={t('components.photoUpload.orPasteImageUrl')}
                            value={urlDraft}
                            disabled={disabled || uploading}
                            style={{ flex: 1 }}
                            onChange={(e) => setUrlDraft(e.currentTarget.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    e.preventDefault();
                                    void addUrl();
                                }
                            }}
                        />
                        <ActionIcon
                            variant='light'
                            disabled={disabled || uploading || !urlDraft.trim()}
                            onClick={() => void addUrl()}
                            aria-label={t('components.photoUpload.addUrl')}
                        >
                            <IconPhotoPlus size={16} />
                        </ActionIcon>
                    </Group>
                </Stack>
            </Group>

            <input
                ref={inputRef}
                type='file'
                accept='image/*'
                hidden
                disabled={disabled || uploading}
                onChange={(e) => {
                    const files = Array.from(e.currentTarget.files || []);

                    e.currentTarget.value = '';
                    if (files.length) void handleFiles(files);
                }}
            />

            {value ? (
                <ImageLightbox
                    urls={[value]}
                    index={0}
                    opened={previewOpen}
                    onClose={() => setPreviewOpen(false)}
                    title={resolvedLabel}
                />
            ) : null}
        </Stack>
    );
};

const GalleryUpload = ({
    label,
    description,
    value,
    onChange,
    coverIndex,
    onCoverIndexChange,
    imageType = ImageTypeEnum.CITY_ATTRACTION,
    disabled,
    max = 24,
}: Omit<GalleryProps, 'variant'>) => {
    const { t } = useTranslation();
    const resolvedLabel = label ?? t('components.photoUpload.photos');
    const inputRef = useRef<HTMLInputElement>(null);
    const addingRef = useRef(false);
    const [uploading, setUploading] = useState(false);
    const [urlDraft, setUrlDraft] = useState('');
    const [previewIndex, setPreviewIndex] = useState<number | null>(null);

    const effectiveCover = coverIndex ?? (value.length ? 0 : null);

    const handleFiles = useCallback(
        async (files: File[]) => {
            const images = files.filter(isImageFile);

            if (!images.length) {
                notifications.show({
                    color: 'red',
                    message: t('components.photoUpload.dropImageFiles'),
                });

                return;
            }

            const room = Math.max(0, max - value.length);

            if (!room) {
                notifications.show({
                    color: 'orange',
                    message: t('components.photoUpload.maxPhotos', { max }),
                });

                return;
            }

            setUploading(true);

            try {
                const urls: string[] = [];

                for (const file of images.slice(0, room)) {
                    urls.push(await uploadImage(file, imageType));
                }

                onChange([...value, ...urls]);
            } catch (error) {
                notifications.show({ color: 'red', message: apiErrorMessage(error) });
            } finally {
                setUploading(false);
            }
        },
        [imageType, max, onChange, value, t],
    );

    const removeAt = (index: number) => {
        const next = value.filter((_, i) => i !== index);

        onChange(next);

        if (!onCoverIndexChange) return;

        if (!next.length) {
            onCoverIndexChange(null);

            return;
        }

        if (effectiveCover == null) return;

        if (index === effectiveCover) onCoverIndexChange(0);
        else if (index < effectiveCover) onCoverIndexChange(effectiveCover - 1);
    };

    const setCover = (index: number) => {
        if (onCoverIndexChange) {
            onCoverIndexChange(index);

            return;
        }

        if (index === 0) return;

        const next = [...value];
        const [picked] = next.splice(index, 1);

        onChange([picked, ...next]);
    };

    const addUrl = async () => {
        const next = urlDraft.trim();

        if (!next || addingRef.current) return;
        if (value.length >= max) {
            notifications.show({
                color: 'orange',
                message: t('components.photoUpload.maxPhotos', { max }),
            });

            return;
        }

        addingRef.current = true;
        setUploading(true);

        try {
            onChange([...value, await ingestImageUrl(next, imageType)]);
            setUrlDraft('');
        } catch {
            notifications.show({
                color: 'red',
                message: t('components.photoUpload.urlUnreadable'),
            });
        } finally {
            addingRef.current = false;
            setUploading(false);
        }
    };

    return (
        <Stack gap='sm'>
            {(resolvedLabel || description) && (
                <Stack gap={2}>
                    {resolvedLabel && (
                        <Text size='sm' fw={500}>
                            {resolvedLabel}
                        </Text>
                    )}
                    {description && (
                        <Text size='xs' c='dimmed'>
                            {description}
                        </Text>
                    )}
                </Stack>
            )}

            {value.length > 0 && (
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                        gap: 10,
                    }}
                >
                    {value.map((url, index) => {
                        const isCover = effectiveCover === index;

                        return (
                            <Box
                                key={`${url}-${index}`}
                                style={{
                                    position: 'relative',
                                    borderRadius: 14,
                                    overflow: 'hidden',
                                    aspectRatio: '1',
                                    border: isCover
                                        ? `2px solid ${ACCENT}`
                                        : `1px solid ${BORDER}`,
                                    boxShadow: isCover
                                        ? '0 6px 18px color-mix(in srgb, var(--mantine-color-brand-6, #228be6) 18%, transparent)'
                                        : 'none',
                                    background: 'var(--mantine-color-gray-1)',
                                }}
                            >
                                <UnstyledButton
                                    onClick={() => setPreviewIndex(index)}
                                    aria-label={t('components.coverUpload.viewFullSize')}
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        height: '100%',
                                        cursor: 'zoom-in',
                                    }}
                                >
                                    <Image
                                        src={url}
                                        alt=''
                                        w='100%'
                                        h='100%'
                                        fit='cover'
                                        referrerPolicy='no-referrer'
                                    />
                                </UnstyledButton>

                                {isCover && (
                                    <Text
                                        size='10px'
                                        fw={700}
                                        c='white'
                                        style={{
                                            position: 'absolute',
                                            left: 8,
                                            top: 8,
                                            zIndex: 1,
                                            padding: '2px 8px',
                                            borderRadius: 999,
                                            background: ACCENT,
                                            letterSpacing: 0.3,
                                            textTransform: 'uppercase',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        {t('components.photoUpload.cover')}
                                    </Text>
                                )}

                                {!disabled && (
                                    <Group
                                        gap={4}
                                        style={{
                                            position: 'absolute',
                                            right: 6,
                                            bottom: 6,
                                            zIndex: 1,
                                        }}
                                    >
                                        <Tooltip
                                            label={
                                                isCover
                                                    ? t('components.photoUpload.cover')
                                                    : t('components.photoUpload.setAsCover')
                                            }
                                        >
                                            <ActionIcon
                                                size='sm'
                                                radius='xl'
                                                variant='white'
                                                color={isCover ? 'yellow' : 'gray'}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setCover(index);
                                                }}
                                                aria-label={
                                                    isCover
                                                        ? t('components.photoUpload.cover')
                                                        : t('components.photoUpload.setAsCover')
                                                }
                                            >
                                                {isCover ? (
                                                    <IconStarFilled size={14} />
                                                ) : (
                                                    <IconStar size={14} />
                                                )}
                                            </ActionIcon>
                                        </Tooltip>
                                        <Tooltip label={t('components.photoUpload.remove')}>
                                            <ActionIcon
                                                size='sm'
                                                radius='xl'
                                                variant='white'
                                                color='red'
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeAt(index);
                                                }}
                                                aria-label={t('components.photoUpload.remove')}
                                            >
                                                <IconTrash size={14} />
                                            </ActionIcon>
                                        </Tooltip>
                                    </Group>
                                )}
                            </Box>
                        );
                    })}
                </Box>
            )}

            <DropShell
                active={false}
                disabled={disabled || uploading || value.length >= max}
                onBrowse={() => inputRef.current?.click()}
                onDropFiles={handleFiles}
                minHeight={120}
            >
                <UnstyledButton
                    disabled={disabled || uploading || value.length >= max}
                    onClick={() => inputRef.current?.click()}
                    style={{ width: '100%', height: '100%' }}
                >
                    <Stack align='center' justify='center' gap={6} py='lg' px='md'>
                        {uploading ? (
                            <Loader size={22} />
                        ) : (
                            <Box
                                style={{
                                    width: 42,
                                    height: 42,
                                    borderRadius: 12,
                                    display: 'grid',
                                    placeItems: 'center',
                                    background: 'var(--mantine-color-white)',
                                    border: `1px solid ${BORDER}`,
                                }}
                            >
                                <IconPhoto size={20} stroke={1.5} color={ACCENT} />
                            </Box>
                        )}
                        <Text size='sm' fw={600}>
                            {uploading
                                ? t('components.photoUpload.uploading')
                                : t('components.photoUpload.dropPhotosHere')}
                        </Text>
                        <Text size='xs' c='dimmed'>
                            {t('components.photoUpload.orClickToBrowse')} · {value.length}/{max}
                        </Text>
                    </Stack>
                </UnstyledButton>
            </DropShell>

            <Group gap='xs' wrap='nowrap'>
                <TextInput
                    size='sm'
                    placeholder={t('components.photoUpload.orPasteUrlEnter')}
                    value={urlDraft}
                    disabled={disabled || uploading || value.length >= max}
                    style={{ flex: 1 }}
                    onChange={(e) => setUrlDraft(e.currentTarget.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            addUrl();
                        }
                    }}
                />
                <ActionIcon
                    variant='light'
                    size='lg'
                    disabled={disabled || uploading || !urlDraft.trim() || value.length >= max}
                    onClick={addUrl}
                    aria-label={t('components.photoUpload.addUrl')}
                >
                    <IconPhotoPlus size={18} />
                </ActionIcon>
            </Group>

            <input
                ref={inputRef}
                type='file'
                accept='image/*'
                multiple
                hidden
                disabled={disabled || uploading}
                onChange={(e) => {
                    const files = Array.from(e.currentTarget.files || []);

                    e.currentTarget.value = '';
                    if (files.length) void handleFiles(files);
                }}
            />

            <ImageLightbox
                urls={value}
                index={previewIndex ?? 0}
                opened={previewIndex != null}
                onClose={() => setPreviewIndex(null)}
                onIndexChange={setPreviewIndex}
                title={resolvedLabel}
            />
        </Stack>
    );
};

export const PhotoUpload = (props: Props) => {
    if (props.variant === 'avatar') {
        return <AvatarUpload {...props} />;
    }

    return <GalleryUpload {...props} />;
};
